/**
 * @file SettingsForm.tsx
 *
 * The Settings form — a faithful replica of WSB-v2's Shop settings on this stack
 * (react-intl, a controlled-state hook, Gravity styling). Built on the shared
 * {@link FormColumnLayout} primitives (heading-above-card sections, `gv-label`
 * fields, native `gv-input-select`), matching the other admin forms. Three
 * sections — Shop information, Email preferences, Region & currency — the last of
 * which also carries our Shopfront language control (WSB-v2 keeps email language
 * only). Owns the editable state via {@link useSettingsFormState}, validates on
 * save, and reports the assembled fields to its parent. Unlike WSB-v2 (a sub-page
 * of Website Settings) this is a top-level tab, so there is no back breadcrumb and
 * the footer has only Save.
 */

import { useId } from 'react';
import type { ChangeEvent, ReactNode, RefObject } from 'react';
import { GeneralField, Icon } from '@group.one/react-gravity';
import { FormattedMessage, useIntl } from 'react-intl';
import { SUPPORTED_LOCALES, isSupportedLocale } from '@wsb/i18n';
import { adminMessages } from '../i18n/messages';
import { FormFooter } from '../components/FormFooter';
import {
  FormColumnLayout,
  FormSection,
  FormSectionHeading,
  FormSectionCard,
} from '../components/form-layout/FormColumnLayout';
import { useSettingsFormState } from './use-settings-form-state';
import {
  CURRENCY_OPTIONS,
  CURRENCY_POSITION_OPTIONS,
  DATE_FORMAT_OPTIONS,
  NUMBER_FORMAT_OPTIONS,
  WEIGHT_UNIT_OPTIONS,
  TIMEZONE_OPTIONS,
} from './settings-constants';
import { EMAIL_LANGUAGE_OPTIONS } from './email-language-constants';
import type { SettingsFormFields, SettingsOption } from './settings-form-types';
import styles from './SettingsForm.module.css';

interface SettingsFormProps {
  /** Seed fields derived from the loaded settings + shopfront locale. */
  seed: SettingsFormFields;
  /** Country `<select>` options (localized), from the region tree. */
  countryOptions: SettingsOption[];
  saving: boolean;
  saveError?: string;
  onSubmit: (fields: SettingsFormFields) => void;
}

/** The labelled wrapper around a single field: label (+ Optional), control, error, helper. */
function FieldShell({
  id,
  label,
  optional = false,
  error,
  helper,
  children,
}: {
  id: string;
  label: string;
  optional?: boolean;
  error?: string;
  helper?: string;
  children: ReactNode;
}) {
  return (
    <div className="gv-form-option gv-w-full">
      <label className="gv-label" htmlFor={id}>
        <span>{label}</span>
        {optional ? (
          <span className="gv-label-optional">
            <FormattedMessage {...adminMessages.formOptional} />
          </span>
        ) : null}
      </label>
      {children}
      {error ? (
        <span className="gv-text-sm gv-state-critical" role="alert">
          {error}
        </span>
      ) : null}
      {helper ? <p className="gv-caption-lg gv-m-0">{helper}</p> : null}
    </div>
  );
}

/** A native `<select>` styled as a Gravity input, with the expand caret. */
function SelectControl({
  id,
  value,
  testId,
  ariaLabel,
  options,
  placeholder,
  onChange,
}: {
  id: string;
  value: string;
  testId: string;
  ariaLabel: string;
  options: SettingsOption[];
  placeholder?: string;
  onChange: (event: ChangeEvent<HTMLSelectElement>) => void;
}) {
  return (
    <div className="gv-input gv-input-select gv-w-full">
      <select
        id={id}
        value={value}
        data-testid={testId}
        aria-label={ariaLabel}
        onChange={onChange}
      >
        {placeholder !== undefined ? <option value="">{placeholder}</option> : null}
        {options.map((option) => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </select>
      <Icon name="expand_more" size="small" />
    </div>
  );
}

/**
 * Renders the Settings form.
 *
 * @param props - Seed fields, country options, save state, and the submit handler.
 *
 * @returns The settings form.
 */
export function SettingsForm({
  seed,
  countryOptions,
  saving,
  saveError,
  onSubmit,
}: SettingsFormProps) {
  const intl = useIntl();
  const baseId = useId();
  const { fields, patch, errors, validate, firstErrorRef } = useSettingsFormState(seed);

  const shopfrontOptions: SettingsOption[] = SUPPORTED_LOCALES.map((locale) => ({
    value: locale,
    label: locale,
  }));
  // A shop may be provisioned with a locale outside the supported set (e.g. `en-GB`). Keep
  // it as an option so the control reflects the real stored value rather than silently
  // showing — and, on the next change, saving — a different supported tag over it.
  const { shopfrontLanguage } = fields;
  if (shopfrontLanguage && !isSupportedLocale(shopfrontLanguage)) {
    shopfrontOptions.unshift({ value: shopfrontLanguage, label: shopfrontLanguage });
  }

  // Same guard for the time zone: the API accepts any zone `Intl` recognises (a superset of
  // the canonical `TIMEZONE_OPTIONS` — aliases like `US/Pacific`, `GMT`, …). Surface a stored
  // non-canonical zone as an option so the control shows the real value instead of silently
  // rendering (and, on the next save, overwriting it with) the first option.
  const timezoneOptions: SettingsOption[] = TIMEZONE_OPTIONS.some((o) => o.value === fields.timezone)
    ? TIMEZONE_OPTIONS
    : [{ value: fields.timezone, label: fields.timezone }, ...TIMEZONE_OPTIONS];

  const handleSave = (): void => {
    if (!validate()) {
      return;
    }
    onSubmit(fields);
  };

  const id = (suffix: string): string => `${baseId}-${suffix}`;

  return (
    <div className={styles.form}>
      <header className="gv-flex gv-flex-col gv-w-full gv-gap-sm">
        <h2 className="gv-heading-lg gv-m-0">
          <FormattedMessage {...adminMessages.settingsTitle} />
        </h2>
      </header>

      <FormColumnLayout>
        <FormSection>
          <FormSectionHeading title={intl.formatMessage(adminMessages.settingsShopInfoTitle)} />
          <FormSectionCard>
            <FieldShell id={id('shop-name')} label={intl.formatMessage(adminMessages.settingsShopName)}>
              <GeneralField
                id={id('shop-name')}
                inputRef={firstErrorRef as RefObject<HTMLInputElement>}
                value={fields.shopName}
                error={errors.shopNameError}
                data-testid="settings-form-shop-name"
                aria-label={intl.formatMessage(adminMessages.settingsShopName)}
                onChange={(event) => patch({ shopName: event.target.value })}
              />
            </FieldShell>

            <div className="gv-flex gv-flex-col gv-tab-flex-row gv-gap-md">
              <div className="gv-flex-1">
                <FieldShell id={id('street')} label={intl.formatMessage(adminMessages.settingsStreetAddress)} optional>
                  <GeneralField
                    id={id('street')}
                    value={fields.streetAddress}
                    data-testid="settings-form-street-address"
                    aria-label={intl.formatMessage(adminMessages.settingsStreetAddress)}
                    onChange={(event) => patch({ streetAddress: event.target.value })}
                  />
                </FieldShell>
              </div>
              <div className="gv-flex-1">
                <FieldShell id={id('zip')} label={intl.formatMessage(adminMessages.settingsZipCode)} optional>
                  <GeneralField
                    id={id('zip')}
                    value={fields.zipCode}
                    data-testid="settings-form-zip-code"
                    aria-label={intl.formatMessage(adminMessages.settingsZipCode)}
                    onChange={(event) => patch({ zipCode: event.target.value })}
                  />
                </FieldShell>
              </div>
            </div>

            <div className="gv-flex gv-flex-col gv-tab-flex-row gv-gap-md">
              <div className="gv-flex-1">
                <FieldShell id={id('city')} label={intl.formatMessage(adminMessages.settingsCity)} optional>
                  <GeneralField
                    id={id('city')}
                    value={fields.city}
                    data-testid="settings-form-city"
                    aria-label={intl.formatMessage(adminMessages.settingsCity)}
                    onChange={(event) => patch({ city: event.target.value })}
                  />
                </FieldShell>
              </div>
              <div className="gv-flex-1">
                <FieldShell id={id('country')} label={intl.formatMessage(adminMessages.settingsCountry)} optional>
                  <SelectControl
                    id={id('country')}
                    value={fields.country}
                    testId="settings-form-country"
                    ariaLabel={intl.formatMessage(adminMessages.settingsCountry)}
                    options={countryOptions}
                    placeholder={intl.formatMessage(adminMessages.settingsCountryPlaceholder)}
                    onChange={(event) => patch({ country: event.target.value })}
                  />
                </FieldShell>
              </div>
            </div>
          </FormSectionCard>
        </FormSection>

        <FormSection>
          <FormSectionHeading
            title={intl.formatMessage(adminMessages.settingsEmailPrefsTitle)}
            description={intl.formatMessage(adminMessages.settingsEmailPrefsDescription)}
          />
          <FormSectionCard>
            <FieldShell id={id('email')} label={intl.formatMessage(adminMessages.settingsEmail)}>
              <GeneralField
                id={id('email')}
                value={fields.email}
                error={errors.emailError}
                data-testid="settings-form-email"
                aria-label={intl.formatMessage(adminMessages.settingsEmail)}
                onChange={(event) => patch({ email: event.target.value })}
              />
            </FieldShell>

            <FieldShell
              id={id('email-language')}
              label={intl.formatMessage(adminMessages.settingsEmailLanguage)}
              error={errors.emailLanguageError}
              helper={intl.formatMessage(adminMessages.settingsEmailLanguageDescription)}
            >
              <SelectControl
                id={id('email-language')}
                value={fields.emailLanguage}
                testId="settings-form-email-language"
                ariaLabel={intl.formatMessage(adminMessages.settingsEmailLanguage)}
                options={EMAIL_LANGUAGE_OPTIONS}
                // Placeholder so an unset language (a shop with no stored `email.language`) renders
                // as a visibly empty prompt, not the first option silently masquerading as chosen —
                // which made the required-field validation block Save on a field that looked filled.
                placeholder={intl.formatMessage(adminMessages.settingsEmailLanguagePlaceholder)}
                onChange={(event) => patch({ emailLanguage: event.target.value })}
              />
            </FieldShell>

            <FieldShell id={id('currency-position')} label={intl.formatMessage(adminMessages.settingsCurrencyPosition)}>
              <SelectControl
                id={id('currency-position')}
                value={fields.currencyPosition}
                testId="settings-form-currency-position"
                ariaLabel={intl.formatMessage(adminMessages.settingsCurrencyPosition)}
                options={CURRENCY_POSITION_OPTIONS}
                onChange={(event) => patch({ currencyPosition: event.target.value })}
              />
            </FieldShell>

            <div className="gv-flex gv-flex-col gv-tab-flex-row gv-gap-md">
              <div className="gv-flex-1">
                <FieldShell id={id('date-format')} label={intl.formatMessage(adminMessages.settingsDateFormat)}>
                  <SelectControl
                    id={id('date-format')}
                    value={fields.dateFormat}
                    testId="settings-form-date-format"
                    ariaLabel={intl.formatMessage(adminMessages.settingsDateFormat)}
                    options={DATE_FORMAT_OPTIONS}
                    onChange={(event) => patch({ dateFormat: event.target.value })}
                  />
                </FieldShell>
              </div>
              <div className="gv-flex-1">
                <FieldShell id={id('number-format')} label={intl.formatMessage(adminMessages.settingsNumberFormat)}>
                  <SelectControl
                    id={id('number-format')}
                    value={fields.numberFormat}
                    testId="settings-form-number-format"
                    ariaLabel={intl.formatMessage(adminMessages.settingsNumberFormat)}
                    options={NUMBER_FORMAT_OPTIONS}
                    onChange={(event) => patch({ numberFormat: event.target.value })}
                  />
                </FieldShell>
              </div>
            </div>
          </FormSectionCard>
        </FormSection>

        <FormSection>
          <FormSectionHeading title={intl.formatMessage(adminMessages.settingsRegionCurrencyTitle)} />
          <FormSectionCard>
            <div className="gv-flex gv-flex-col gv-tab-flex-row gv-gap-md">
              <div className="gv-flex-1">
                <FieldShell id={id('currency')} label={intl.formatMessage(adminMessages.settingsCurrency)} error={errors.currencyError}>
                  <SelectControl
                    id={id('currency')}
                    value={fields.currency}
                    testId="settings-form-currency"
                    ariaLabel={intl.formatMessage(adminMessages.settingsCurrency)}
                    options={CURRENCY_OPTIONS}
                    onChange={(event) => patch({ currency: event.target.value })}
                  />
                </FieldShell>
              </div>
              <div className="gv-flex-1">
                <FieldShell id={id('weight-unit')} label={intl.formatMessage(adminMessages.settingsWeightUnit)}>
                  <SelectControl
                    id={id('weight-unit')}
                    value={fields.weightUnit}
                    testId="settings-form-weight-unit"
                    ariaLabel={intl.formatMessage(adminMessages.settingsWeightUnit)}
                    options={WEIGHT_UNIT_OPTIONS}
                    onChange={(event) => patch({ weightUnit: event.target.value })}
                  />
                </FieldShell>
              </div>
            </div>

            <FieldShell
              id={id('shopfront-language')}
              label={intl.formatMessage(adminMessages.settingsShopfrontTitle)}
              helper={intl.formatMessage(adminMessages.settingsShopfrontDescription)}
            >
              <SelectControl
                id={id('shopfront-language')}
                value={fields.shopfrontLanguage}
                testId="settings-form-shopfront-language"
                ariaLabel={intl.formatMessage(adminMessages.settingsShopfrontTitle)}
                options={shopfrontOptions}
                onChange={(event) => patch({ shopfrontLanguage: event.target.value })}
              />
            </FieldShell>

            <FieldShell
              id={id('timezone')}
              label={intl.formatMessage(adminMessages.settingsTimezone)}
              helper={intl.formatMessage(adminMessages.settingsTimezoneDescription)}
            >
              <SelectControl
                id={id('timezone')}
                value={fields.timezone}
                testId="settings-form-timezone"
                ariaLabel={intl.formatMessage(adminMessages.settingsTimezone)}
                options={timezoneOptions}
                onChange={(event) => patch({ timezone: event.target.value })}
              />
            </FieldShell>
          </FormSectionCard>
        </FormSection>

        {saveError ? (
          <p
            className="gv-text-sm gv-state-critical"
            role="alert"
            data-testid="settings-form-error"
          >
            {saveError}
          </p>
        ) : null}

        <FormFooter>
          <button
            type="button"
            className="gv-button gv-button-primary"
            data-testid="settings-form-save"
            onClick={handleSave}
            disabled={saving}
          >
            {saving ? (
              <FormattedMessage {...adminMessages.settingsSaving} />
            ) : (
              <FormattedMessage {...adminMessages.settingsSave} />
            )}
          </button>
        </FormFooter>
      </FormColumnLayout>
    </div>
  );
}
